import React, { useState, useEffect, useRef } from "react";
import {
  Sparkles,
  Star,
  Mic,
  BookOpen,
  Volume2,
  Trash2,
  PenTool,
  Ruler,
  Eraser,
  Hash, // Icon for Number activity
  Calculator, // Icon for Quiz activity
  Binary, // Icon for Tracing activity
  Tally1Icon, // Icon for Counting activity
  Timer, // Icon for Coming Soon
  Music, // Replaced Hash for Song Video
  Video, // Replaced Hash for Song Video
} from "lucide-react";

// ======================= SPEECH UTILITY FUNCTION (Reused) =======================
const speakText = (text, selectedVoice) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.8;
    utterance.pitch = 1.2;

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    window.speechSynthesis.speak(utterance);
  } else {
    console.warn("Speech Synthesis not supported in this browser.");
  }
};


// ======================= TRACING BOARD COMPONENT (Adapted for Numbers) =======================
// ======================= UPDATED TRACING BOARD =======================
// ======================= FIXED TRACING BOARD =======================
const TracingBoard = ({ number: initialNumber, countingWord, selectedVoice }) => {
  const canvasRef = useRef(null);
  
  // 1. Local State for Selection (Since we can't use parent's setNumber)
  const [currentNum, setCurrentNum] = useState(initialNumber || "0");
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState('#D97706'); 
  const [penSize, setPenSize] = useState(10);
  const [tracingMode, setTracingMode] = useState('guide');

  const numbers = "0123456789".split("");

  const speak = (t) => {
    if (typeof speakText === "function") {
      speakText(t.toString().toLowerCase(), selectedVoice);
    }
  };

  const refreshCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    drawNumberGuide(ctx, rect.width, rect.height, tracingMode);
  };

  // 2. Updated Effect: Watch local 'currentNum' instead of prop 'number'
  useEffect(() => {
    refreshCanvas();
    window.addEventListener('resize', refreshCanvas);
    return () => window.removeEventListener('resize', refreshCanvas);
  }, [currentNum, tracingMode]);

  const drawNumberGuide = (ctx, width, height, mode) => {
    ctx.clearRect(0, 0, width, height);
    if (mode === "practice") return;

    ctx.strokeStyle = "rgba(0,0,0,0.05)";
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();
    ctx.setLineDash([]);

    const fontSize = height * 0.8;
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = "rgba(217,119,6,0.15)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    
    // Draw the local current number
    ctx.fillText(currentNum, width / 2, height / 2);
  };

  // 3. Updated Handler to use local state
  const handleNumberChange = (num) => {
    setCurrentNum(num); // Update local active number
    speak(num);        // Speak the number
  };

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    if (e.cancelable) e.preventDefault(); 

    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = penSize;
    ctx.strokeStyle = penColor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  return (
    <div className="flex flex-col border-2 border-orange-200 rounded-[2.5rem] bg-white shadow-2xl overflow-hidden">
      
      {/* 🔢 THE SELECTOR: Now uses 'currentNum' for highlighting */}
      <div className="flex overflow-x-auto gap-3 p-4 bg-orange-50 border-b no-scrollbar">
        {numbers.map((num) => (
          <button
            key={num}
            onClick={() => handleNumberChange(num)}
            className={`flex-shrink-0 w-12 h-12 rounded-2xl text-xl font-black transition-all active:scale-95
              ${num.toString() === currentNum.toString() 
                ? "bg-orange-600 text-white shadow-lg scale-110 -translate-y-1" 
                : "bg-white border-2 border-orange-100 text-orange-400 hover:border-orange-300"
              }`}
          >
            {num}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center p-4 border-b bg-white">
        <button 
           onClick={() => setTracingMode(tracingMode === 'guide' ? 'practice' : 'guide')}
           className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-xl font-bold text-sm"
        >
          {tracingMode === 'guide' ? "🎓 GUIDED" : "✏️ FREEHAND"}
        </button>

        <button onClick={refreshCanvas} className="flex items-center gap-1 p-2 text-pink-500 font-bold hover:bg-pink-50 rounded-lg transition-colors">
           <Trash2 size={18} /> CLEAR
        </button>
      </div>

      {/* Canvas Container */}
      <div className="relative aspect-video touch-none bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none cursor-crosshair block"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => setIsDrawing(false)}
          onMouseLeave={() => setIsDrawing(false)}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => setIsDrawing(false)}
        />
      </div>
    </div>
  );
};

// ======================= ACTIVITY DISPLAY COMPONENT (Updated default case) =======================
const NumberActivityDisplay = ({ activity, number, countingWords }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    setNum1(Math.floor(Math.random() * 10));
    setNum2(Math.floor(Math.random() * 10));
    setResult(null);
    setUserAnswer('');
  }, [number, activity]);

  const checkAnswer = () => {
    const correctAnswer = num1 + num2;
    const isCorrect = parseInt(userAnswer) === correctAnswer;
    setResult(isCorrect);
  };

  // Helper to generate an array of icons for the current number
  const renderCountingIcons = () => {
    const count = parseInt(number);
    if (isNaN(count)) return null;

    const icons = [];

    for (let i = 0; i < count; i++) {
      icons.push(
        <span key={i} className="text-4xl text-orange-600 animate-bounce transition duration-150">
          ⭐
        </span>
      );
    }
    return icons;
  };


  if (!activity) {
    return (
      <div className="text-center p-10 bg-orange-50 rounded-2xl shadow-inner text-gray-600">
        <Calculator className="w-10 h-10 text-fuchsia-500 mx-auto mb-3" />
        <p className="text-lg font-semibold">
          Select an activity from the sidebar menu to start your interactive learning for {number}!
        </p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activity.key) {
      case "tracing":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-orange-600">Digital Number Tracing</h4>
            <TracingBoard
              number={number}
              setNumber={() => { }} // Number changes via main component
              countingWord={countingWords[number]}
            />
            <p className="text-gray-700">
              Practice writing the number {number} using the guide.
            </p>
          </div>
        );
      case "counting":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-teal-600">
              Counting Activity: Count the Stars!
            </h4>
            <div className="bg-teal-100 p-6 rounded-lg shadow-md flex justify-center flex-wrap gap-4 min-h-24 border border-teal-300">
              {renderCountingIcons()}
            </div>
            <p className="text-gray-700">
              The number {number} represents {countingWords[number]} stars!
              {number === '0' && " That's zero stars."}
            </p>
            <button
              onClick={() => speakText(`Count to ${number}`, null)}
              className="bg-teal-600 text-white px-4 py-2 rounded-xl hover:bg-teal-700 font-semibold"
            >
              <Volume2 className="w-4 h-4 mr-2 inline-block" /> Hear the Count
            </button>
          </div>
        );
      case "quiz":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-fuchsia-600">
              Simple Addition Quiz
            </h4>
            <div className="bg-fuchsia-100 p-5 rounded-lg shadow-md border border-fuchsia-300">
              <p className="text-2xl font-bold mb-4 text-center text-fuchsia-800">
                {num1} + {num2} = ?
              </p>
              <div className="flex items-center justify-center gap-3">
                <input
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  className="w-20 p-2 text-center border-2 border-fuchsia-400 rounded-lg text-xl"
                  placeholder="?"
                />
                <button
                  onClick={checkAnswer}
                  className="bg-fuchsia-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-fuchsia-700 transition"
                >
                  Check
                </button>
              </div>
              {result !== null && (
                <p className={`mt-3 text-lg font-bold text-center ${result ? 'text-green-600' : 'text-red-600'}`}>
                  {result ? '✅ Correct! Great job!' : `❌ Try again! The answer is ${num1 + num2}.`}
                </p>
              )}
            </div>
          </div>
        );
      default:
        return (
          <div className="text-center p-10 bg-gray-50 rounded-2xl shadow-inner text-gray-500">
            <Timer className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
            <p className="text-lg font-semibold">
              {activity.title} is coming soon for number {number}!
            </p>
          </div>
        );
    }
  };

  return (
    <div className="bg-white p-6 rounded-3xl shadow-2xl border border-gray-100 space-y-4">
      <h3 className="text-3xl font-extrabold text-fuchsia-700 border-b pb-3 mb-4">
        {activity.title}
      </h3>
      {renderContent()}
    </div>
  );
};

// ======================= ACTIVITY CARD COMPONENT (Restored with status logic) =======================
const ActivityCard = ({ title, icon, color, activityKey, onClick, isActive, status }) => {
  const isComingSoon = status === 'coming_soon';

  const handleClick = (e) => {
    if (isComingSoon) {
      console.log(`${title} is coming soon!`);
      // If clicked on a disabled card, we still trigger onClick to update the main screen
      onClick(activityKey, title);
    } else {
      onClick(activityKey, title);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`p-4 rounded-xl shadow-md flex items-center gap-3 transition cursor-pointer border-l-4
        ${isComingSoon
          ? 'bg-gray-200 border-gray-400 text-gray-500 opacity-60 grayscale cursor-not-allowed'
          : isActive
            ? 'bg-fuchsia-100 border-fuchsia-500 ring-2 ring-fuchsia-300 font-bold text-gray-800'
            : 'bg-white border-orange-100 hover:bg-orange-50 text-gray-800'
        }
      `}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${color}`}>{icon}</div>
      <div className="flex-1">
        <h4 className={`text-base font-semibold ${isComingSoon ? 'line-through-none' : ''}`}>
          {title}
          {isComingSoon && (
            <span className="ml-2 inline-flex items-center text-xs font-bold text-yellow-700 bg-yellow-300/50 px-2 py-0.5 rounded-full ring-1 ring-yellow-400">
              <Timer className="w-3 h-3 mr-1" /> Soon
            </span>
          )}
        </h4>
      </div>
    </div>
  );
};


// ======================= MAIN PAGE COMPONENT =======================
const NumbersAndMathsPage = () => {
  const numbers = "0123456789".split("");
  const countingWords = {
    0: "Zero", 1: "One star", 2: "Two ducks", 3: "Three cars", 4: "Four apples",
    5: "Five trees", 6: "Six clouds", 7: "Seven flowers", 8: "Eight balls", 9: "Nine kites"
  };

  const [selectedNumber, setSelectedNumber] = useState("1");
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [story, setStory] = useState("");

  const [voices, setVoices] = useState([]);
  const [preferredVoice, setPreferredVoice] = useState(null);
  const voiceListRef = useRef([]);

  const currentCountingWord = countingWords[selectedNumber] || "Something";

  // UPDATED ACTIVITIES: Last two are 'coming_soon'
  const activities = [
    { key: "tracing", title: "Number Tracing", icon: <Binary className="text-white w-5 h-5" />, color: "bg-orange-500", status: 'live' },
    { key: "counting", title: "Interactive Counting", icon: <Tally1Icon className="text-white w-5 h-5" />, color: "bg-teal-500", status: 'live' },
    { key: "quiz", title: "Simple Math Quiz", icon: <Calculator className="text-white w-5 h-5" />, color: "bg-fuchsia-500", status: 'live' },
    { key: "story", title: "Number Stories", icon: <BookOpen className="text-white w-5 h-5" />, color: "bg-pink-500", status: 'coming_soon' },
    { key: "video", title: "Number Song Video", icon: <Video className="text-white w-5 h-5" />, color: "bg-blue-500", status: 'coming_soon' },
  ];

  // Simple static story generator
  const createStory = (number, word) => {
    return `
      One day, the number ${number} woke up and decided to go for a walk. 
      On the way, ${word} joined in. They all marched happily, proving 
      that math is everywhere! ${number} is a truly special number.
    `;
  };

  // ASYNCHRONOUS VOICE LOADING (Reused)
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const currentVoices = window.speechSynthesis.getVoices();
        if (currentVoices.length > 0 && voiceListRef.current.length === 0) {
          voiceListRef.current = currentVoices;
          setVoices(currentVoices);

          const preferred = currentVoices.find(v =>
            v.lang.startsWith('en-') &&
            (v.name.includes('Kid') || v.name.includes('Zarvox') || v.name.includes('US English'))
          ) || currentVoices.find(v => v.lang.startsWith('en-'));

          setPreferredVoice(preferred);
        }
      };

      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
      loadVoices();
    }
  }, []);

  const handleNumberClick = (num) => {
    setSelectedNumber(num);
    speakText(`Number ${num}`, preferredVoice);
  };

  const handleKeywordSpeak = () => {
    const speechText = `${selectedNumber}. That is, ${countingWords[selectedNumber]}`;
    speakText(speechText, preferredVoice);
  };

  useEffect(() => {
    setStory(createStory(selectedNumber, currentCountingWord));
    setSelectedActivity(null);
  }, [selectedNumber]);

  const handleActivityClick = (key, title) => {
    // Find the full activity object to check status
    const activity = activities.find(a => a.key === key);
    setSelectedActivity(activity); // Always set to show status in main content
  };

  const handleVoiceChange = (event) => {
    const voiceName = event.target.value;
    const newVoice = voices.find(v => v.name === voiceName);
    setPreferredVoice(newVoice);
    speakText(selectedNumber, newVoice);
  };


  return (
    <div className="min-h-screen bg-[#FFFBF8] p-5 font-sans">

      {/* ======================= HEADER ======================= */}
      <div className="w-full mb-8 bg-white p-6 rounded-3xl shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          {/* Title Block */}
          <div className="flex items-center gap-4 mb-2 sm:mb-0">
            <Calculator className="w-8 h-8 text-fuchsia-500" />
            <h1 className="text-4xl md:text-5xl font-extrabold text-orange-700 tracking-tight">
              Numbers & Math Hub
            </h1>
          </div>

          {/* VOICE SELECTION DROPDOWN */}
          {'speechSynthesis' in window && (
            <div className="flex items-center space-x-2 bg-fuchsia-100 p-2 rounded-xl mt-3 sm:mt-0 max-w-full">
              <Mic className="w-5 h-5 text-fuchsia-600 flex-shrink-0" />
              <label htmlFor="voice-select" className="text-sm font-semibold text-fuchsia-700 flex-shrink-0">Voice:</label>
              <select
                id="voice-select"
                onChange={handleVoiceChange}
                value={preferredVoice ? preferredVoice.name : ''}
                className="p-1 rounded bg-white border border-fuchsia-300 text-sm text-gray-700 w-full sm:w-auto min-w-40"
                disabled={voices.length === 0}
              >
                {voices.length === 0 ? (
                  <option>Loading voices...</option>
                ) : (
                  voices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))
                )}
              </select>
            </div>
          )}
        </div>
        <p className="text-gray-600 text-lg mt-2">
          Master numbers, counting, tracing, and basic arithmetic with fun activities.
        </p>
      </div>

      {/* ======================= MAIN CONTENT GRID ======================= */}
      <div className="w-full grid lg:grid-cols-4 gap-8">

        {/* ======================= LEFT COLUMN (SIDEBAR) ======================= */}
        <div className="lg:col-span-1 space-y-6">

          {/* NUMBER PICKER CONTAINER */}
          <div>
            <h2 className="text-xl font-bold mb-3 text-orange-700 border-l-4 border-orange-400 pl-3">
              Choose a Number:
            </h2>
            <div className="bg-white rounded-3xl p-4 shadow-xl border border-orange-200 space-y-4">
              {/* Number Grid */}
              <div className="grid grid-cols-5 md:grid-cols-6 lg:grid-cols-4 gap-2">
                {numbers.map((num) => (
                  <button
                    key={num}
                    onClick={() => handleNumberClick(num)}
                    className={`rounded-xl py-3 text-lg font-bold transition duration-200 transform hover:scale-105
                            ${selectedNumber === num
                        ? "bg-fuchsia-600 text-white shadow-xl ring-4 ring-fuchsia-300 border-b-4 border-fuchsia-800"
                        : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                      }
                            `}
                  >
                    {num}
                  </button>
                ))}
              </div>

              {/* NUMBER-FOR-WORD BUTTON */}
              <button
                onClick={handleKeywordSpeak}
                className={`w-full flex items-center justify-center p-3 rounded-xl font-bold transition duration-200 shadow-lg 
                    ${selectedNumber
                    ? 'bg-fuchsia-500 text-white hover:bg-fuchsia-600'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  }
                    `}
                disabled={!selectedNumber}
              >
                <Volume2 className="w-5 h-5 mr-2" />
                {selectedNumber} means {currentCountingWord}
              </button>
            </div>
          </div>

          {/* ACTIVITY SIDEBAR */}
          <div>
            <h2 className="text-xl font-bold mb-3 text-orange-700 border-l-4 border-fuchsia-400 pl-3">
              Math Activities:
            </h2>
            <div className="space-y-3">
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.key}
                  title={activity.title}
                  icon={activity.icon}
                  color={activity.color}
                  activityKey={activity.key}
                  onClick={handleActivityClick}
                  isActive={selectedActivity?.key === activity.key}
                  status={activity.status} // Pass the status
                />
              ))}
            </div>
          </div>

        </div>

        {/* ======================= RIGHT COLUMN (MAIN CONTENT) ======================= */}
        <div className="lg:col-span-3 space-y-8">

          {/* -------------------- HERO NUMBER CARD & COUNTING -------------------- */}
          <div className="bg-white p-6 rounded-3xl shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center border-b pb-4 mb-4 border-orange-100">
              <div className="flex items-end gap-4">
                <h2 className="text-8xl font-extrabold text-orange-700 drop-shadow-md leading-none">
                  {selectedNumber}
                </h2>
                <h3 className="text-4xl font-light text-fuchsia-500 leading-none pb-2">
                  {currentCountingWord}
                </h3>
              </div>

              {/* Replay number sound */}
              <button
                onClick={() => speakText(`The number is ${selectedNumber}`, preferredVoice)}
                className="flex items-center bg-orange-100 px-4 py-2 rounded-full shadow-inner hover:bg-orange-200 transition"
                title="Click to hear the number"
              >
                <Mic className="w-6 h-6 text-orange-600 mr-2" />
                <span className="text-xl font-semibold text-orange-700">
                  Say Number
                </span>
              </button>
            </div>

            {/* Place Value / Basic Concept */}
            <h3 className="text-lg font-bold mb-3 text-gray-700">
              Basic Concept:
            </h3>
            <div className="flex flex-wrap gap-3">
              <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-semibold text-base shadow-sm border border-teal-300">
                One more is {parseInt(selectedNumber) + 1}
              </span>
              <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-semibold text-base shadow-sm border border-teal-300">
                {selectedNumber} is {parseInt(selectedNumber) % 2 === 0 && selectedNumber !== '0' ? 'Even' : 'Odd'}
              </span>
              <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-semibold text-base shadow-sm border border-teal-300">
                It comes after {Math.max(0, parseInt(selectedNumber) - 1)}
              </span>
            </div>
          </div>

          {/* -------------------- STORY SECTION -------------------- */}
          <div className="bg-white p-6 rounded-3xl shadow-xl border border-fuchsia-100">
            <div className="flex items-center mb-4">
              <BookOpen className="w-6 h-6 text-fuchsia-500 mr-3" />
              <h3 className="text-2xl font-bold text-orange-700">
                Story Time: The {selectedNumber} Adventure
              </h3>
            </div>

            <div className="bg-fuchsia-50 p-4 rounded-xl shadow-inner text-gray-700 leading-relaxed text-base border border-fuchsia-100">
              <p
                dangerouslySetInnerHTML={{
                  __html: story.replace(/\*\*(.*?)\*\*/g, "<strong class='text-fuchsia-600 font-extrabold'>$1</strong>")
                }}
              />
            </div>
            <button className="mt-4 w-full bg-fuchsia-600 text-white px-4 py-2 rounded-xl font-semibold shadow-md hover:bg-fuchsia-700 transition">
              Read More & Listen
            </button>
          </div>

          {/* -------------------- MAIN ACTIVITY SCREEN -------------------- */}
          <NumberActivityDisplay
            activity={selectedActivity}
            number={selectedNumber}
            countingWords={countingWords}
          />
        </div>
      </div>
    </div>
  );
};

export default NumbersAndMathsPage;